import React, { useState, useEffect } from 'react';
import { useModuleCatalog } from '../../hooks/useModuleCatalog';
import { ModuleCard } from './ModuleCard';
import { RefreshCw, Shield, Puzzle, Zap, Radio, Database } from 'lucide-react';
import { WebRtcInspector } from '../../admin/extensions/WebRtcInspector';
import { LedgerAuditor } from '../../admin/extensions/LedgerAuditor';
import { ModuleManifest } from '../../services/ModuleScanner';

export const SuperadminDashboard: React.FC<{ onClose?: () => void }> = () => {
  const { coreModules, decoupledModules, loading } = useModuleCatalog();
  const [refreshing, setRefreshing] = useState(false);
  const [hardcodedModules, setHardcodedModules] = useState<any[]>([]);
  const [activeCatalogTab, setActiveCatalogTab] = useState<'modules' | 'webrtc' | 'ledger'>('modules');

  useEffect(() => {
    fetch('/api/v1/admin/modules/available')
      .then(res => res.json())
      .then(data => {
        const list = data?.data?.modules || data?.modules || [];
        if (Array.isArray(list) && list.length > 0) {
          setHardcodedModules(list);
        }
      })
      .catch(() => {});
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await fetch('/api/modules/refresh', { method: 'POST' });
    } catch (err) {
      console.error('Failed to refresh modules:', err);
    } finally {
      setRefreshing(false);
    }
  };

  const handleToggle = async (moduleId: string, enabled: boolean) => {
    try {
      await fetch(`/api/modules/${moduleId}/toggle`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled }),
      });
    } catch (err) {
      console.error('Failed to toggle module:', err);
    }
  };

  const mergedDecoupled: ModuleManifest[] = decoupledModules;

  if (loading && mergedDecoupled.length === 0) {
    return (
      <div style={{ padding: '32px', color: 'var(--theme-text-muted, #94a3b8)', textAlign: 'center' }}>
        Loading module registry...
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <div style={{
        background: 'linear-gradient(135deg, rgba(56, 189, 248, 0.1), rgba(16, 185, 129, 0.1))',
        border: '1px solid var(--theme-border, #1e293b)',
        borderRadius: '12px',
        padding: '20px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ background: 'rgba(56, 189, 248, 0.2)', padding: '10px', borderRadius: '8px' }}>
            <Zap size={22} color="var(--theme-accent, #38bdf8)" />
          </div>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <h2 style={{ fontSize: '16px', fontWeight: 700, color: 'var(--theme-text-primary, #f1f5f9)' }}>
                Sovereign Module Hub & Microkernel Controller
              </h2>
              <span style={{ fontSize: '10px', background: 'rgba(56, 189, 248, 0.2)', color: 'var(--theme-accent, #38bdf8)', padding: '2px 6px', borderRadius: '4px', fontWeight: 600 }}>
                v1.2.0
              </span>
            </div>
            <p style={{ fontSize: '11px', color: 'var(--theme-text-secondary, #94a3b8)', marginTop: '4px' }}>
              Verified server-side registry. Cryptographically validated with Ed25519 signatures and SHA-256 asset hashes.
            </p>
          </div>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '8px 14px',
            borderRadius: '6px',
            border: '1px solid var(--theme-border, #1e293b)',
            background: 'var(--theme-bg-card, #151d2a)',
            color: 'var(--theme-accent, #38bdf8)',
            fontSize: '11px',
            fontWeight: 600,
            cursor: refreshing ? 'not-allowed' : 'pointer',
            opacity: refreshing ? 0.6 : 1,
          }}
        >
          <RefreshCw size={13} className={refreshing ? 'animate-spin' : ''} />
          <span>{refreshing ? 'Syncing...' : '🔄 Sync'}</span>
        </button>
      </div>

      

       {/* Pluggable Extension & Catalog Tabs */}
       <div style={{ display: 'flex', gap: '8px', borderBottom: '1px solid var(--theme-border, #1e293b)', paddingBottom: '12px' }}>
         <button
           type="button"
           onClick={() => setActiveCatalogTab('modules')}
           style={{
             display: 'flex',
             alignItems: 'center',
             gap: '6px',
             padding: '8px 14px',
             borderRadius: '8px',
             fontSize: '12px',
             fontWeight: 600,
             cursor: 'pointer',
             border: activeCatalogTab === 'modules' ? '1px solid var(--theme-accent, #38bdf8)' : '1px solid var(--theme-border, #1e293b)',
             background: activeCatalogTab === 'modules' ? 'rgba(56, 189, 248, 0.15)' : 'var(--theme-bg-card, #151d2a)',
             color: activeCatalogTab === 'modules' ? 'var(--theme-accent, #38bdf8)' : 'var(--theme-text-secondary, #94a3b8)',
           }}
         >
           <Shield size={14} />
           <span>Kernel & Modules</span>
         </button>

         <button
           type="button"
           onClick={() => setActiveCatalogTab('webrtc')}
           style={{
             display: 'flex',
             alignItems: 'center',
             gap: '6px',
             padding: '8px 14px',
             borderRadius: '8px',
             fontSize: '12px',
             fontWeight: 600,
             cursor: 'pointer',
             border: activeCatalogTab === 'webrtc' ? '1px solid #10b981' : '1px solid var(--theme-border, #1e293b)',
             background: activeCatalogTab === 'webrtc' ? 'rgba(16, 185, 129, 0.15)' : 'var(--theme-bg-card, #151d2a)',
             color: activeCatalogTab === 'webrtc' ? '#34d399' : 'var(--theme-text-secondary, #94a3b8)',
           }}
         >
           <Radio size={14} />
           <span>📡 WebRTC & Media Relay</span>
         </button>

         <button
           type="button"
           onClick={() => setActiveCatalogTab('ledger')}
           style={{
             display: 'flex',
             alignItems: 'center',
             gap: '6px',
             padding: '8px 14px',
             borderRadius: '8px',
             fontSize: '12px',
             fontWeight: 600,
             cursor: 'pointer',
             border: activeCatalogTab === 'ledger' ? '1px solid #a855f7' : '1px solid var(--theme-border, #1e293b)',
             background: activeCatalogTab === 'ledger' ? 'rgba(168, 85, 247, 0.15)' : 'var(--theme-bg-card, #151d2a)',
             color: activeCatalogTab === 'ledger' ? '#c084fc' : 'var(--theme-text-secondary, #94a3b8)',
           }}
         >
           <Database size={14} />
           <span>🪙 Postgres Ledger & HXN</span>
         </button>
       </div>

       {/* TAB 2: WebRTC Inspector Extension */}
       {activeCatalogTab === 'webrtc' && (
         <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
           <WebRtcInspector />
         </div>
       )}

       {/* TAB 3: Postgres Ledger Auditor Extension */}
       {activeCatalogTab === 'ledger' && (
         <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
           <LedgerAuditor />
         </div>
       )}

       {/* TAB 1: Core & Decoupled Modules */}
       {activeCatalogTab === 'modules' && (
         <>
       <section style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Shield size={16} color="var(--theme-accent, #38bdf8)" />
          <h3 style={{ fontSize: '14px', fontWeight: 700, color: 'var(--theme-text-primary, #f1f5f9)' }}>
            Core Kernel Services ({coreModules.length})
          </h3>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '12px' }}>
          {coreModules.map((mod) => (
            <ModuleCard key={mod.id} module={mod} isCore={true} />
          ))}
        </div>
      </section>

      <section style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Puzzle size={16} color="#34d399" />
          <h3 style={{ fontSize: '14px', fontWeight: 700, color: 'var(--theme-text-primary, #f1f5f9)' }}>
            Sovereign Decoupled Modules ({mergedDecoupled.length})
          </h3>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '12px' }}>
          {mergedDecoupled.map((mod) => (
            <ModuleCard key={mod.id} module={mod} isCore={false} onToggle={handleToggle} />
          ))}
        </div>
      </section>
            </>
      )}
</div>
  );
};
